import * as babylon from "@babylonjs/core";

import { interpolateGradientColor } from "../../assetBuilders/gradient";
import type * as components from "../../components";
import { BabylonState } from "../../state";

import {
  sampleAt,
  interpolationOf,
  type Interpolation,
} from "./setPositionsToTime";

/** Reused as the origin point for the current-vector mesh segment each frame. */
const VECTOR_ORIGIN = Object.freeze(babylon.Vector3.Zero());

/**
 * The cached vector at `time`, as a fresh vector: lerped between the bracketing
 * samples when `interpolation` blends that pair (see `interpolatesBetween`),
 * and otherwise the latest sample at or before `time`.
 *
 * Clamps outside the cache's range in either mode, as `localPositionAt` does —
 * before the first sample yields the first vector, after the last yields the
 * last. Two samples sharing a timestamp yield the earlier one rather than a NaN
 * fraction. Always allocates, so the result can be handed to the current-vector
 * mesh without aliasing the cache.
 */
export function vectorAt(
  cache: Pick<components.VectorField["cache"], "logtimes" | "allVectors">,
  time: bigint,
  interpolation: Interpolation,
): babylon.Vector3 | undefined {
  const bracket = sampleAt(cache.logtimes, time, interpolation);
  if (bracket === undefined) {
    return undefined;
  }
  const lastVec = cache.allVectors.at(bracket.index);
  if (lastVec === undefined) {
    return undefined;
  }
  const nextVec = cache.allVectors.at(bracket.index + 1);
  if (bracket.fraction === undefined || nextVec === undefined) {
    return lastVec.clone();
  }
  return babylon.Vector3.Lerp(lastVec, nextVec, bracket.fraction);
}

/**
 * Syncs each entity's vector field to the current time.
 *
 * Samples the current vector from the vector field cache, blended or held as
 * the panel's data settings say, and updates the currentMesh geometry and
 * gradient color from it. The settings are read every frame, so a change to
 * them needs no restaling here. The field-wide mesh is not sampled at all: it
 * draws every recorded vector at its own time, placed from the baked global
 * trajectory, which `syncDataSettings` restales instead.
 */
export function updateVectorFields(state: BabylonState) {
  const time = state.sceneConfig.time;
  if (time === undefined) {
    return;
  }
  const interpolation = interpolationOf(state);
  for (const entity of Object.values(state.entities)) {
    if (
      !entity.vectorField ||
      entity.vectorField.cache.load.state !== "loaded" ||
      entity.vectorField.cache.logtimes.length === 0
    ) {
      continue;
    }

    const current = vectorAt(entity.vectorField.cache, time, interpolation);
    if (current === undefined) {
      continue;
    }
    entity.vectorField.current = current;

    // Update the currentMesh geometry to reflect the new vector.
    if (entity.vectorField.currentMesh) {
      const segment = [VECTOR_ORIGIN, entity.vectorField.current];
      entity.vectorField.currentMesh.setPoints(segment);

      // Interpolate color based on gradient when enabled.
      const gradient = entity.vectorField.gradient;
      if (gradient) {
        const mag = entity.vectorField.current.length();
        if (Number.isFinite(mag)) {
          const material = entity.vectorField.currentMesh.greasedLineMaterial;
          if (material) {
            material.color = interpolateGradientColor(gradient, mag);
          }
        }
      }
    }
  }
}
